import type {
  DataProvider,
  BaseRecord,
  GetListParams,
  GetListResponse,
  GetOneParams,
  GetOneResponse,
  CreateParams,
  CreateResponse,
  UpdateParams,
  UpdateResponse,
  DeleteOneParams,
  DeleteOneResponse,
} from "@refinedev/core";

import api from "../services/api";
import { API_URL } from "../config";

/**
 * A small Refine DataProvider that adapts your existing API shapes.
 *
 * Why this exists:
 * - Your backend endpoints don’t follow Refine’s default REST conventions
 *   (some return arrays, some return `{ menus }`, etc.)
 * - We want to retain existing API behavior and just map it for the Admin UI.
 */
export const adminDataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  getList: async <TData extends BaseRecord = BaseRecord>(
    params: GetListParams,
  ): Promise<GetListResponse<TData>> => {
    const { resource, pagination, filters } = params;
    const current = pagination?.current ?? 1;
    const pageSize = pagination?.pageSize ?? 10;

    // Extract search filter if present
    const searchFilter = filters?.find((f) => 
      'field' in f && (f.field === "q" || f.field === "search")
    );
    const searchQuery = searchFilter && 'value' in searchFilter 
      ? (searchFilter.value as string | undefined)
      : undefined;

    // Build query parameters
    const queryParams = new URLSearchParams({
      page: current.toString(),
      pageSize: pageSize.toString(),
    });
    if (searchQuery) {
      queryParams.append("search", searchQuery);
    }

    let response: any;

    if (resource === "restaurants") {
      const res = await api.get(`/restaurants?${queryParams.toString()}`);
      // Backend returns { data, total, page, pageSize }
      response = res.data;
    } else if (resource === "menus") {
      const res = await api.get(`/menus?${queryParams.toString()}`);
      // Backend returns { data, total, page, pageSize }
      response = res.data;
    } else {
      // Generic fallback: try `/resource` and expect an array.
      const res = await api.get(`/${resource}?${queryParams.toString()}`);
      response = res.data;
    }

    // Handle both old format (array) and new format (object with data/total)
    if (Array.isArray(response)) {
      return {
        data: response as TData[],
        total: response.length,
      };
    }

    // Handle new paginated format
    if (response && typeof response === 'object' && 'data' in response) {
      return {
        data: (Array.isArray(response.data) ? response.data : []) as TData[],
        total: typeof response.total === 'number' ? response.total : 0,
      };
    }

    // Fallback for unexpected format
    return {
      data: [] as TData[],
      total: 0,
    };
  },

  getOne: async <TData extends BaseRecord = BaseRecord>(
    params: GetOneParams,
  ): Promise<GetOneResponse<TData>> => {
    const { resource, id } = params;

    if (resource === "restaurants") {
      const res = await api.get(`/restaurants/${id}`);
      return { data: (res.data?.restaurant ?? res.data) as TData };
    }

    if (resource === "menus") {
      const res = await api.get(`/menus/${id}`);
      return { data: (res.data?.menu ?? res.data) as TData };
    }

    if (resource === "ingredients") {
      const res = await api.get(`/ingredients/${id}`);
      return { data: (res.data?.ingredient ?? res.data) as TData };
    }

    const res = await api.get(`/${resource}/${id}`);
    return { data: res.data as TData };
  },

  create: async <TData extends BaseRecord = BaseRecord, TVariables = {}>(
    params: CreateParams<TVariables>,
  ): Promise<CreateResponse<TData>> => {
    const { resource } = params;
    if (resource === "restaurants") {
      const res = await api.post("/restaurants", params.variables);
      return { data: (res.data?.restaurant ?? res.data) as TData };
    }

    if (resource === "ingredients") {
      const res = await api.post("/ingredients", params.variables);
      return { data: (res.data?.ingredient ?? res.data) as TData };
    }

    throw new Error(`Create is not supported for resource: ${resource}`);
  },

  update: async <TData extends BaseRecord = BaseRecord, TVariables = {}>(
    params: UpdateParams<TVariables>,
  ): Promise<UpdateResponse<TData>> => {
    const { resource, id, variables } = params;

    if (resource === "restaurants") {
      const res = await api.put(`/restaurants/${id}`, variables);
      return { data: (res.data?.restaurant ?? res.data) as TData };
    }

    if (resource === "menus") {
      const res = await api.put(`/menus/${id}`, variables);
      return { data: (res.data?.menu ?? res.data) as TData };
    }

    if (resource === "ingredients") {
      const res = await api.put(`/ingredients/${id}`, variables);
      return { data: (res.data?.ingredient ?? res.data) as TData };
    }

    const res = await api.put(`/${resource}/${id}`, variables);
    return { data: res.data as TData };
  },

  deleteOne: async <TData extends BaseRecord = BaseRecord, TVariables = {}>(
    params: DeleteOneParams<TVariables>,
  ): Promise<DeleteOneResponse<TData>> => {
    const { resource, id } = params;

    await api.delete(`/${resource}/${id}`);

    // Many of your delete endpoints return counts, not the deleted record.
    // Refine mainly needs an object back; we return `{ id }` for consistency.
    return { data: { id } as unknown as TData };
  },
};
